import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { useLanguage } from '../contexts/LanguageContext';
import {
  fetchPaidBooks,
  getSignedBookUrl,
  BookAccessError,
  PaidBook,
} from '../api/paidBookApi';
import { enableSecureScreen, disableSecureScreen } from '../utils/screenCapture';
import './PaidBookReaderScreen.css';

// ─── The secured reader for purchased PDF books ──────────────────────────────
//
// Everything here exists to keep one invariant: the bytes of a paid book reach
// the screen and nowhere else. Concretely, that rules out the approach the free
// reader (EbookReaderScreen) takes, and it is worth being explicit about why:
//
//  1. NO Google Docs gview iframe. The free reader embeds
//     docs.google.com/gview?url=<pdf>, which hands the URL to a Google server
//     that fetches the file itself, and renders a viewer with its own download
//     and pop-out buttons. For a paid book that would ship a live bearer
//     credential to a third party and then offer the user a Download button.
//  2. NO "open externally" button. window.open() on the signed URL puts the file
//     in Chrome, which downloads it to /sdcard/Download. That single tap would
//     defeat the entire model.
//  3. NO file on disk. The PDF is fetched into an ArrayBuffer, handed straight to
//     pdf.js, and painted to a <canvas>. Nothing touches Filesystem, no blob: URL
//     is created (a blob: URL can be long-pressed and saved), and the signed URL
//     is never written to state, storage or a log — it lives in one local const
//     for the length of one fetch.
//  4. Canvas, not a text layer. Without pdf.js's text layer there is no
//     selectable text, so no copy, and the long-press menu has nothing to offer.
//
// What this buys is that the file cannot be exported. It is NOT DRM: see the
// honest limitations at the bottom of this file.

type Lang = 'bn' | 'it' | 'en';

const CONTENT = {
  bn: {
    opening: 'বই খোলা হচ্ছে...',
    decoding: 'পৃষ্ঠা তৈরি হচ্ছে...',
    lockedTitle: 'এই বইটি কেনা হয়নি',
    lockedBody: 'বইটি পড়তে আগে কিনতে হবে। আপনাকে বইয়ের তালিকায় ফেরত নেওয়া হচ্ছে...',
    signInTitle: 'সাইন ইন করুন',
    signInBody: 'কেনা বই দেখতে আপনার অ্যাকাউন্টে সাইন ইন করুন।',
    signIn: 'সাইন ইন',
    unavailableTitle: 'বইটি এখন খোলা যাচ্ছে না',
    unavailableBody: 'ইন্টারনেট সংযোগ দেখে আবার চেষ্টা করুন।',
    retry: 'আবার চেষ্টা করুন',
    back: 'বইয়ের তালিকায় ফিরুন',
    book: 'বই',
    page: 'পৃষ্ঠা',
    of: '/',
    prev: 'আগের পৃষ্ঠা',
    next: 'পরের পৃষ্ঠা',
    zoom: 'জুম',
    secure: 'স্ক্রিনশট বন্ধ',
  },
  it: {
    opening: 'Apertura del libro...',
    decoding: 'Preparazione della pagina...',
    lockedTitle: 'Libro non acquistato',
    lockedBody: 'Devi acquistare questo libro per leggerlo. Ti riportiamo alla libreria...',
    signInTitle: 'Accedi',
    signInBody: 'Accedi al tuo account per aprire i libri che hai acquistato.',
    signIn: 'Accedi',
    unavailableTitle: 'Impossibile aprire il libro',
    unavailableBody: 'Controlla la connessione e riprova.',
    retry: 'Riprova',
    back: 'Torna alla Libreria',
    book: 'Libro',
    page: 'Pagina',
    of: '/',
    prev: 'Pagina precedente',
    next: 'Pagina successiva',
    zoom: 'Zoom',
    secure: 'Screenshot disattivati',
  },
  en: {
    opening: 'Opening book...',
    decoding: 'Preparing page...',
    lockedTitle: 'Book not purchased',
    lockedBody: 'You need to buy this book before you can read it. Taking you back to the library...',
    signInTitle: 'Sign in',
    signInBody: 'Sign in to your account to open the books you have purchased.',
    signIn: 'Sign in',
    unavailableTitle: 'Could not open this book',
    unavailableBody: 'Check your connection and try again.',
    retry: 'Try again',
    back: 'Back to Library',
    book: 'Book',
    page: 'Page',
    of: '/',
    prev: 'Previous page',
    next: 'Next page',
    zoom: 'Zoom',
    secure: 'Screenshots off',
  },
};

// ─── pdf.js bootstrap ────────────────────────────────────────────────────────
//
// Loaded with a dynamic import so webpack splits it into its own chunk: pdf.js
// is ~380 KB and only the handful of users who own a book ever pay for it. This
// app has no lazy routes, so this is the one place that split happens.
//
// The worker is served as a STATIC file from public/, not resolved through
// webpack. It has to be, and the reason is worth recording:
//
// `new URL('…/pdf.worker.min.js', import.meta.url)` looks like the modern,
// correct approach, and webpack 5 does emit the asset. But CRA's babel-loader
// also transpiles it on the way through, prepending @babel/runtime helpers as
// absolute require() paths from the build machine:
//
//   var _defineProperty = require("/Users/…/node_modules/@babel/runtime/…").default
//
// A Web Worker has no `require`, so it threw "ReferenceError: require is not
// defined" on line 1, pdf.js fell back to a fake worker, that failed too, and
// the reader reported "could not open the book" for what looked like a network
// problem. Those absolute paths would not have existed on a phone regardless.
//
// public/ files are copied verbatim, never parsed, so nothing can rewrite them.
// scripts/copy-pdf-worker.js refreshes the copy on every build, so it cannot
// drift from the installed pdfjs-dist version.

let pdfjsPromise: Promise<typeof import('pdfjs-dist/legacy/build/pdf')> | null = null;

function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then((pdfjsLib) => {
      // PUBLIC_URL is '' under Capacitor, giving '/pdf.worker.min.js', which the
      // WebView serves from the app's own assets at https://localhost.
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return pdfjsLib;
    });
  }
  return pdfjsPromise;
}

// Screens are 1080–1440 px wide; rendering at more than 2x that is invisible and
// costs 4 bytes per pixel of WebView heap, which is what actually OOMs on cheap
// Android hardware.
const MAX_PIXEL_RATIO = 2;
const ZOOM_STEPS = [1, 1.5, 2.25];

// The "not purchased" state is a dead end, so it returns the user to the library
// by itself; the button is for anyone who does not want to wait.
const AUTO_RETURN_MS = 3500;

type Phase = 'loading' | 'ready' | 'locked' | 'signin' | 'unavailable';

const PaidBookReaderScreen: React.FC = () => {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const { currentLanguage } = useLanguage();
  const lang = currentLanguage as Lang;
  const t = CONTENT[lang] || CONTENT.en;

  const [phase, setPhase] = useState<Phase>('loading');
  const [book, setBook] = useState<PaidBook | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [rendering, setRendering] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const docRef = useRef<PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const getLang = (ml: { it: string; bn: string; en: string }) => ml[lang] || ml.en || ml.it || '';

  // ── FLAG_SECURE for exactly as long as this screen is mounted ──────────────
  // Separate effect from the loading one, and with no dependencies, so the flag
  // goes up before a single pixel of book can be painted and comes down on every
  // exit path — including the error states and the hardware back button.
  useEffect(() => {
    enableSecureScreen();
    return () => {
      disableSecureScreen();
    };
  }, []);

  // ── Fetch the entitlement, the bytes, and the document ─────────────────────
  useEffect(() => {
    if (!productId) {
      setPhase('unavailable');
      return;
    }

    let cancelled = false;
    setPhase('loading');

    (async () => {
      try {
        // Titles come from the public catalogue, access from the Edge Function.
        // Deliberately not awaited and deliberately independent: a catalogue
        // hiccup must never look like a failed purchase. fetchPaidBooks() already
        // resolves to [] instead of rejecting, so a miss simply leaves the title
        // on its generic fallback while the book still opens.
        fetchPaidBooks().then((books) => {
          if (cancelled) return;
          setBook(books.find((b) => b.play_product_id === productId) || null);
        });

        const pdfjsLib = await loadPdfjs();

        // The only authority on whether this user may read this book. A 403 here
        // is the server saying no, and the client has no way to overrule it.
        const signedUrl = await getSignedBookUrl(productId);
        if (cancelled) return;

        // One shot, straight to memory. `signedUrl` is not stored anywhere and
        // goes out of scope with this function; the ~300 s expiry then makes the
        // string worthless even if it were somehow captured.
        // no-store, not the default. Supabase Storage sends a cache-control
        // max-age, so Chromium would otherwise write the whole PDF into the
        // app's cache directory keyed by the signed URL — outliving the 300s
        // TTL of the URL that fetched it, and contradicting the no-file-on-disk
        // guarantee this screen is built around.
        const response = await fetch(signedUrl, { cache: 'no-store' });
        if (!response.ok) throw new BookAccessError('unavailable', `PDF fetch ${response.status}`);
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (cancelled) return;

        const doc = await pdfjsLib.getDocument({
          data: bytes,
          // CVE-2024-4367: a crafted PDF can run script through the font path when
          // eval is available. Our own books are not the threat, but there is no
          // reason to leave the door open in a WebView that also holds the user's
          // Supabase session.
          isEvalSupported: false,
          // No annotation/JS execution, and no external resource loading.
          disableAutoFetch: true,
        }).promise;
        if (cancelled) {
          doc.destroy();
          return;
        }

        docRef.current = doc;
        setPageCount(doc.numPages);
        setPageNum(1);
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        // Never surface a raw error to the reader. Every failure is one of three
        // human sentences, chosen by reason, with the detail left in the console.
        const reason = err instanceof BookAccessError ? err.reason : 'unavailable';
        if (reason === 'not-purchased') setPhase('locked');
        else if (reason === 'unauthenticated') setPhase('signin');
        else setPhase('unavailable');
        console.warn('[paid-reader] could not open book', reason);
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
      // Tears down the pdf.js worker too, which is where the decoded page data
      // lives. Leaving it alive would keep a whole book in memory after exit.
      docRef.current?.destroy();
      docRef.current = null;
    };
  }, [productId, attempt]);

  // ── Send the user back when they are not allowed in ────────────────────────
  useEffect(() => {
    if (phase !== 'locked') return;
    const timer = setTimeout(() => navigate('/ebooks', { replace: true }), AUTO_RETURN_MS);
    return () => clearTimeout(timer);
  }, [phase, navigate]);

  // ── Page rendering ─────────────────────────────────────────────────────────
  const renderPage = useCallback(async (n: number, zoom: number) => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!doc || !canvas || !stage) return;

    // A second render into the same canvas while the first is in flight paints
    // garbage, so the previous task is always cancelled first.
    renderTaskRef.current?.cancel();
    setRendering(true);

    let page;
    try {
      page = await doc.getPage(n);
    } catch {
      setRendering(false);
      return;
    }

    const unscaled = page.getViewport({ scale: 1 });
    const stageWidth = stage.clientWidth || unscaled.width;
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const cssScale = (stageWidth / unscaled.width) * zoom;
    const viewport = page.getViewport({ scale: cssScale * dpr });

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    // Backing store is at device resolution, layout box is in CSS pixels — this
    // is what keeps Bengali and Italian diacritics crisp instead of mushy.
    canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
    canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setRendering(false);
      return;
    }

    try {
      const task = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      await task.promise;
    } catch {
      // A cancelled render is the normal path when the user pages quickly.
    } finally {
      renderTaskRef.current = null;
      page.cleanup();
      setRendering(false);
    }
  }, []);

  useEffect(() => {
    if (phase !== 'ready') return;
    renderPage(pageNum, ZOOM_STEPS[zoomIndex]);
  }, [phase, pageNum, zoomIndex, renderPage]);

  // Rotation and split-screen change the stage width, which changes the render
  // scale; without this the page stays at the old width until the user turns it.
  useEffect(() => {
    if (phase !== 'ready') return;
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => renderPage(pageNum, ZOOM_STEPS[zoomIndex]));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [phase, pageNum, zoomIndex, renderPage]);

  const goToPage = (n: number) => {
    if (n < 1 || n > pageCount) return;
    setPageNum(n);
    stageRef.current?.scrollTo({ top: 0, left: 0 });
  };

  const title = book ? getLang(book.title_ml) : t.book;

  // ── Non-reading states ─────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="paid-reader-screen state">
        <div className="paid-reader-spinner" />
        <p>{t.opening}</p>
      </div>
    );
  }

  if (phase === 'locked' || phase === 'signin' || phase === 'unavailable') {
    const copy = {
      locked: { icon: '🔒', title: t.lockedTitle, body: t.lockedBody },
      signin: { icon: '👤', title: t.signInTitle, body: t.signInBody },
      unavailable: { icon: '📕', title: t.unavailableTitle, body: t.unavailableBody },
    }[phase];

    return (
      <div className="paid-reader-screen state">
        <span className="paid-reader-state-icon">{copy.icon}</span>
        <h2 className="paid-reader-state-title">{copy.title}</h2>
        <p className="paid-reader-state-body">{copy.body}</p>
        <div className="paid-reader-state-actions">
          {phase === 'signin' && (
            <button className="paid-reader-btn primary" onClick={() => navigate('/login')}>
              {t.signIn}
            </button>
          )}
          {phase === 'unavailable' && (
            <button className="paid-reader-btn primary" onClick={() => setAttempt((a) => a + 1)}>
              {t.retry}
            </button>
          )}
          <button className="paid-reader-btn" onClick={() => navigate('/ebooks', { replace: true })}>
            {t.back}
          </button>
        </div>
      </div>
    );
  }

  // ── Reading ────────────────────────────────────────────────────────────────
  //
  // onContextMenu is suppressed across the whole screen: Android's WebView
  // long-press menu is the last remaining built-in export affordance, and while
  // a <canvas> gives it nothing to save, blocking it removes the invitation.

  return (
    <div className="paid-reader-screen" onContextMenu={(e) => e.preventDefault()}>
      <div className="paid-reader-topbar">
        <button
          className="paid-reader-back"
          onClick={() => navigate('/ebooks')}
          aria-label={t.back}
        >
          ←
        </button>
        <div className="paid-reader-title-wrap">
          <h2>{title}</h2>
          {/* Told up front, because a black screenshot otherwise reads as a bug. */}
          <span className="paid-reader-secure-badge">🔒 {t.secure}</span>
        </div>
        {/* Deliberately no share / download / open-externally button here. */}
      </div>

      {/* Body/stage split mirrors EbookReaderScreen: the stage is the scroller, so
          the spinner has to live outside it or it pans away with the page. */}
      <div className="paid-reader-body">
        <div className="paid-reader-stage" ref={stageRef}>
          <canvas className="paid-reader-canvas" ref={canvasRef} />
        </div>
        {rendering && (
          <div className="paid-reader-page-loading">
            <div className="paid-reader-spinner small" />
          </div>
        )}
      </div>

      <div className="paid-reader-bottombar">
        <button
          className="paid-reader-nav"
          onClick={() => goToPage(pageNum - 1)}
          disabled={pageNum <= 1}
          aria-label={t.prev}
        >
          ‹
        </button>
        <span className="paid-reader-pageinfo">
          {t.page} {pageNum} {t.of} {pageCount}
        </span>
        <button
          className="paid-reader-nav"
          onClick={() => goToPage(pageNum + 1)}
          disabled={pageNum >= pageCount}
          aria-label={t.next}
        >
          ›
        </button>
        <button
          className="paid-reader-zoom"
          onClick={() => setZoomIndex((i) => (i + 1) % ZOOM_STEPS.length)}
          aria-label={t.zoom}
        >
          {ZOOM_STEPS[zoomIndex]}×
        </button>
      </div>
    </div>
  );
};

export default PaidBookReaderScreen;
